import { useEffect, useState } from 'react';

/** Returns true when the key is held down, except if it's in an input or textarea */
export const useHeldKey = (key: string, allowInput?: boolean): boolean => {
  const [isHeld, setIsHeld] = useState<boolean>(false);

  useEffect(() => {
    const MODIFIER_KEYS = ['Shift', 'Control', 'Alt', 'Meta'];
    const isModifierKey = MODIFIER_KEYS.includes(key);

    // We must do this because in some scenarios (e.g. native macos system shortcuts)
    // the window does not lose focus (no blur event) yet there is no keyup event
    const onMouseMove = (e: MouseEvent) => {
      if (!isModifierKey) {
        return;
      }
      if (key === 'Shift' && !e.shiftKey) {
        onNoLongerHeld();
      }
      if (key === 'Control' && !e.ctrlKey) {
        onNoLongerHeld();
      }
      if (key === 'Alt' && !e.altKey) {
        onNoLongerHeld();
      }
      if (key === 'Meta' && !e.metaKey) {
        onNoLongerHeld();
      }
    };
    const onNoLongerHeld = () => {
      setIsHeld(false);
      window.removeEventListener('mousemove', onMouseMove);
    };

    const onKeyDown = (e: KeyboardEvent) => {
      const targetElementName =
        (e.target instanceof HTMLElement && e.target.nodeName.toLowerCase()) || '';
      const allowed = allowInput || !['input', 'textarea'].includes(targetElementName);
      const matches = e.key === key && allowed;
      if (!matches) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      setIsHeld(true);
      window.addEventListener('mousemove', onMouseMove);
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key === key) {
        onNoLongerHeld();
      }
    };
    const onBlur = () => onNoLongerHeld();
    document.body.addEventListener('keyup', onKeyUp);
    document.body.addEventListener('keydown', onKeyDown);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('blur', onBlur);
      document.body.removeEventListener('keydown', onKeyDown);
      document.body.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('mousemove', onMouseMove);
    };
  }, [key, allowInput]);

  return isHeld;
};
